import { Component, Input, Output, EventEmitter, signal, computed, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { SignaturePadComponent } from '@app/common-codes/components/nested-components/signature-pad/signature-pad.component';
import { TranslatableItem } from '@app/common-codes/components/nested-components/translatable-entity-modal/translatable-entity.interface';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { SafeHtmlPipe } from '@app/common-codes/components/directives/SafeHtmlPipe';

export interface HealthyViewerAcceptEvent {
    accepted: boolean;
    signatureFile?: File;
    answers: { itemId: number; answer: boolean }[];
    hasSymptoms: boolean;
}

@Component({
    selector: 'app-healthy-viewer',
    standalone: true,
    imports: [FormsModule, TranslatePipe, SafeHtmlPipe, SignaturePadComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './healthy-viewer.component.html'
})
export class HealthyViewerComponent implements OnInit, OnChanges {

    /** Items (covid/health questions) avec leurs translations */
    @Input({ required: true }) items: TranslatableItem[] = [];

    /** typeDeTraduction pour le titre (default: 1) */
    titleTranslationType: number = 1;

    /** typeDeTraduction pour le contenu HTML (default: 2) */
    contentTranslationType: number = 2;

    /** Afficher la signature */
    @Input() showSignature: boolean = true;

    /** Rendre la signature obligatoire pour accepter */
    @Input() requireSignature: boolean = true;

    @Output() accepted = new EventEmitter<HealthyViewerAcceptEvent>();
    @Output() refused = new EventEmitter<void>();
    @Output() signatureChange = new EventEmitter<File | null>();

    expandedItemId = signal<number | null>(null);
    /** Map itemId -> true (Oui) / false (Non) / undefined (pas répondu) */
    answers = signal<Map<number, boolean>>(new Map());
    allAccepted = signal(false);
    signatureFile = signal<File | null>(null);

    private currentLangKey: string = 'en';

    allAnswered = computed(() => {
        const ans = this.answers();
        return this.items.length > 0 && this.items.every(item => ans.has(item.id));
    });

    allChecked = computed(() => {
        return this.allAnswered();
    });

    hasSymptoms = computed(() => {
        const ans = this.answers();
        return Array.from(ans.values()).some(v => v === true);
    });

    canAccept = computed(() => {
        if (!this.allAnswered()) return false;
        if (!this.allAccepted()) return false;
        if (this.requireSignature && !this.signatureFile()) return false;
        return true;
    });

    constructor(private translationService: TranslationService) { }

    ngOnInit(): void {
        this.currentLangKey = this.translationService.currentLanguage;
        this.translationService.currentLanguage$.subscribe(lang => {
            this.currentLangKey = lang;
        });
        // Ouvrir le premier item par défaut
        if (this.items.length > 0) {
            this.expandedItemId.set(this.items[0].id);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items']) {
            this.answers.set(new Map());
            this.allAccepted.set(false);
            this.signatureFile.set(null);
            if (this.items.length > 0) {
                this.expandedItemId.set(this.items[0].id);
            }
        }
    }

    /** Retourne le titre (typeDeTraduction=1) d'un item dans la langue courante */
    getTitle(item: TranslatableItem): string {
        const translations = item.translations || [];
        let t = translations.find(
            tr => tr.typeDeTraduction === this.titleTranslationType && tr.language?.key === this.currentLangKey
        );
        if (!t && this.currentLangKey !== 'en') {
            t = translations.find(
                tr => tr.typeDeTraduction === this.titleTranslationType && tr.language?.key === 'en'
            );
        }
        if (!t) {
            t = translations.find(tr => tr.typeDeTraduction === this.titleTranslationType);
        }
        return t?.translationValue || item.description || `Item #${item.id}`;
    }

    /** Retourne le contenu HTML (typeDeTraduction=2) d'un item dans la langue courante */
    getContent(item: TranslatableItem): string {
        const translations = item.translations || [];
        let t = translations.find(
            tr => tr.typeDeTraduction === this.contentTranslationType && tr.language?.key === this.currentLangKey
        );
        if (!t && this.currentLangKey !== 'en') {
            t = translations.find(
                tr => tr.typeDeTraduction === this.contentTranslationType && tr.language?.key === 'en'
            );
        }
        if (!t) {
            t = translations.find(tr => tr.typeDeTraduction === this.contentTranslationType);
        }
        return t?.translationValue || '';
    }

    toggleExpand(itemId: number): void {
        this.expandedItemId.update(current => current === itemId ? null : itemId);
    }

    isExpanded(itemId: number): boolean {
        return this.expandedItemId() === itemId;
    }

    getAnswer(itemId: number): boolean | undefined {
        return this.answers().get(itemId);
    }

    /** Répondre Oui ou Non à une question, puis ouvrir la suivante */
    setAnswer(itemId: number, value: boolean): void {
        this.answers.update(current => {
            const next = new Map(current);
            next.set(itemId, value);
            return next;
        });

        // Auto-ouvrir l'accordéon suivant
        const currentIndex = this.items.findIndex(i => i.id === itemId);
        if (currentIndex >= 0 && currentIndex < this.items.length - 1) {
            const nextItem = this.items[currentIndex + 1];
            this.expandedItemId.set(nextItem.id);
        } else {
            this.expandedItemId.set(null);
        }

        // Reset la checkbox globale si on change une réponse
        if (this.allAccepted()) {
            this.allAccepted.set(false);
        }

        this.emitState();
    }

    onAllAcceptedChange(checked: boolean): void {
        this.allAccepted.set(checked);
        this.emitState();
    }
    onSignatureChange(file: File | null): void {
        this.signatureFile.set(file);
        this.signatureChange.emit(file);
        this.emitState();
    }

    /** Émet l'état courant vers le parent à chaque changement */
    private emitState(): void {
        const answersArray = Array.from(this.answers()).map(([itemId, answer]) => ({ itemId, answer }));
        this.accepted.emit({
            accepted: this.canAccept(),
            signatureFile: this.signatureFile() ?? undefined,
            answers: answersArray,
            hasSymptoms: this.hasSymptoms()
        });
    }
}